/**
 * 现场会面（与分包商 / 公用事业公司 planner 约在现场见）的展示与文案口径。
 *
 * 需求背景：工程周历原先只有「市检预约」一种日程，约 sub 或 utility 的 planner
 * 到现场碰头这类计划无处登记。会面与市检共用同一条周历，但两者业务性质不同：
 * 市检是 City 验收（有上午/下午与通过/整改结果），会面只是碰头安排（有具体时刻、无验收）。
 * 所以这里给会面单独一套标签与配色，避免用户把一次会面误读成一次市检验收。
 *
 * 视觉取舍：市检沿用品牌色（brand），会面改用中性 slate 并配 Users 图标。
 * 不用绿色因为绿色在本系统代表「已完成 / 通过」，会把普通会面误读成验收通过；
 * 也不只靠颜色区分 —— 每张会面卡都带「会面 / Meeting」文字标签和图标。
 */
import React from "react";
import { CheckCircle2, Users } from "lucide-react";

/** 会面类型的中英文口径；后端只存 key，文案在前端统一，避免两端各写一份翻译漂移。 */
export const MEETING_KINDS = {
  subcontractor: ["分包商", "Subcontractor"],
  utility: ["公用事业公司", "Utility"],
  city: ["City / 市政", "City"],
  owner: ["业主", "Owner"],
  other: ["其他", "Other"],
};

/** 状态只有三种事实：已安排、已完成、已取消；会面没有「通过 / 整改」这类验收状态。 */
export const MEETING_STATUS = {
  planned: ["已安排", "Scheduled"],
  done: ["已完成", "Done"],
  cancelled: ["已取消", "Cancelled"],
};

export const meetingKindLabel = (kind, zh) => MEETING_KINDS[kind]?.[zh ? 0 : 1] || (zh ? "会面" : "Meeting");
export const meetingStatusLabel = (status, zh) => MEETING_STATUS[status]?.[zh ? 0 : 1] || status;

/** 时刻展示：只约到天时显示「时间待定」，不编造具体钟点骗人。 */
export const meetingTimeLabel = (row, zh) => {
  if (!row?.startTime) return zh ? "时间待定" : "Time TBD";
  const [h, m] = row.startTime.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return row.startTime;
  // 用 12 小时制加 AM/PM：美国工地同事看 14:00 的反应比 2:00 PM 慢。
  const suffix = h < 12 ? "AM" : "PM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${suffix}`;
};

/** 「几点 · 多久」合并展示，时长未知时只显示时刻，不留空括号。 */
export const meetingWhenLabel = (row, zh) => {
  const when = meetingTimeLabel(row, zh);
  const minutes = Number(row?.durationMinutes) || 0;
  if (!minutes) return when;
  // 90 分钟按 1.5 小时展示，整点小时不带小数：工地口语里「一个半小时」比「90 分钟」常见。
  const duration = minutes >= 60
    ? `${Number((minutes / 60).toFixed(1))} ${zh ? "小时" : "hr"}`
    : `${minutes} ${zh ? "分钟" : "min"}`;
  return `${when} · ${duration}`;
};

/** 外部单位展示：公司库选中时展示库内名称，手填时展示手填名称，不拼接重复信息。 */
export const meetingExternalLabel = (row, zh) => row?.externalName || (zh ? "外部单位待填" : "External party TBD");

/**
 * 周历格子里的一张会面卡。
 *
 * canOpen 为 false 时不提供点击（后端未授权详情），但卡片本身仍可见，
 * 让同组同事知道这个时段有安排，避免重复约同一时间。
 */
export default function SiteMeetingCard({ row, zh, onOpen }) {
  const isDone = row.status === "done";
  const body = <>
    <span className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 gap-y-1">
      <span className="flex shrink-0 items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-700">
        <Users aria-hidden="true" className="h-3 w-3" />{zh ? "会面" : "Meeting"}
      </span>
      <span className="shrink-0 whitespace-nowrap rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold tabular-nums text-slate-700">{meetingTimeLabel(row, zh)}</span>
      {isDone && <span className="flex shrink-0 items-center gap-0.5 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700"><CheckCircle2 aria-hidden="true" className="h-3 w-3" />{meetingStatusLabel("done", zh)}</span>}
      <span className="min-w-0 break-words text-sm font-semibold leading-5 text-slate-900 [overflow-wrap:anywhere]">{row.title}</span>
    </span>
    <span className="block break-words text-xs leading-5 text-slate-600 [overflow-wrap:anywhere]">
      {meetingKindLabel(row.kind, zh)} · {meetingExternalLabel(row, zh)}
      {row.externalContact ? `（${row.externalContact}${row.externalPhone ? ` · ${row.externalPhone}` : ""}）` : (row.externalPhone ? `（${row.externalPhone}）` : "")}
    </span>
  </>;
  const className = `min-w-0 space-y-1 rounded-xl border p-2 text-left ${isDone ? "border-slate-200 bg-slate-50" : "border-slate-200 bg-white"}`;
  // 不能打开时用 div 而不是 disabled button：disabled 按钮在部分读屏器里会被跳过，
  // 用普通容器更符合「这条能看到但读不到详情」的真实语义。
  if (!row.canOpen || !onOpen) return <div className={className} data-testid={`weekly-meeting-${row.id}`}>{body}</div>;
  return <button type="button" className={`${className} min-h-11 w-full transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 sm:min-h-0`} data-testid={`weekly-meeting-${row.id}`} onClick={() => onOpen(row.id)}>{body}</button>;
}

/** 会面在「今日 / 本周」摘要里的一行紧凑表述，供列表型入口复用。 */
export const meetingSummaryLine = (row, zh) => `${meetingWhenLabel(row, zh)} · ${row.title} · ${meetingExternalLabel(row, zh)}`;
